/**
 * Centralized configuration constants for LexiContest client application
 *
 * Provides type-safe constants for animations, WebSocket configuration,
 * UI settings, and game logic parameters with environment support.
 */

/**
 * Animation timing constants
 *
 * CRITICAL: JavaScript timeout values must match corresponding CSS animation durations
 * to ensure proper synchronization between JS state changes and CSS animations.
 */
export const ANIMATION_CONFIG = {
  /** Letter reveal animation duration (matches CSS animation in LetterGrid) */
  LETTER_REVEAL_DURATION: 400,
  /** Button loading feedback duration */
  BUTTON_FEEDBACK_DURATION: 100,
  /** Error state display duration */
  ERROR_STATE_DURATION: 1000,
  /** Progress update animation interval */
  PROGRESS_UPDATE_INTERVAL: 500,
  /** General countdown interval */
  COUNTDOWN_INTERVAL: 1000,
  /** Delay between sequential letter reveals in the animation queue (ms) */
  LETTER_QUEUE_STAGGER_MS: 200,
} as const;

/**
 * WebSocket connection configuration
 *
 * Supports environment-specific URLs and connection parameters
 */
export const WEBSOCKET_CONFIG = {
  /** Default WebSocket URL for development */
  DEFAULT_URL: 'ws://localhost:3000/ws',
  /** Maximum number of reconnection attempts */
  RECONNECT_ATTEMPTS: 3,
  /** Base delay between reconnection attempts (ms) */
  RECONNECT_DELAY: 1000,
  /** Maximum delay between reconnection attempts (ms) - caps exponential backoff */
  MAX_RECONNECT_DELAY: 30000,
  /** Exponential backoff factor for reconnection delays */
  BACKOFF_FACTOR: 2,
  /** Delay before disconnecting inactive connections (ms) */
  DISCONNECT_DELAY: 1000,
} as const;

/**
 * UI configuration constants
 */
export const UI_CONFIG = {
  /** Progress thresholds for different UI states */
  PROGRESS_THRESHOLDS: {
    NEAR_COMPLETE: 95,
    COMPLETE: 100,
    HIGH_PROGRESS: 80,
    LOW_PROGRESS: 30,
  },
  /** Input validation limits */
  VALIDATION: {
    /** Minimum player name length */
    PLAYER_NAME_MIN_LENGTH: 2,
    /** Maximum player name length */
    PLAYER_NAME_MAX_LENGTH: 14,
    /** ASCII alphanumeric only (matches server validation) */
    DISPLAY_NAME_PATTERN: /^[a-zA-Z0-9]+$/,
  },
} as const;

/**
 * Game logic timing configuration
 */
export const GAME_CONFIG = {
  /** Extended search timeout (10 seconds) */
  EXTENDED_SEARCH_TIMEOUT: 10000,
  /** Maximum search timeout (1 minute) */
  MAX_SEARCH_TIMEOUT: 60000,
  /** Game duration display text */
  GAME_DURATION_TEXT: '30 Seconds',
  /** Minimum time to display the match-found screen (ms) */
  MATCH_FOUND_DISPLAY_MS: 2000,
} as const;

/**
 * Development server configuration
 *
 * Security-focused configuration for Vite development server
 */
export const DEV_SERVER_CONFIG = {
  /** Localhost-only to prevent accidental network exposure */
  DEFAULT_HOST: 'localhost',
  DEFAULT_PORT: 5174,
  /** Avoid privileged ports below 1024 */
  MIN_PORT: 1024,
  MAX_PORT: 65535,
  /** Valid host values for security validation */
  VALID_HOSTS: ['localhost', '127.0.0.1', '0.0.0.0'] as readonly string[],
} as const;

/**
 * Authentication configuration
 */
export const AUTH_CONFIG = {
  /** Delay before showing loading spinner during auth operations (ms) */
  LOADING_SPINNER_DELAY: 500,
  /** Simulated delay for mock auth API calls (ms) */
  MOCK_API_DELAY: 400,
  /** Minimum password length */
  PASSWORD_MIN_LENGTH: 8,
  /** Check whether mock auth is enabled via env var */
  isMockAuthEnabled: (): boolean => {
    return import.meta.env.VITE_MOCK_AUTH === 'true';
  },
} as const;

/**
 * Environment-specific configuration
 */
export const ENV_CONFIG = {
  /** Current environment */
  environment: import.meta.env.MODE || 'development',

  /** Get WebSocket URL based on environment */
  getWebSocketUrl: (): string => {
    // Runtime override for integration tests (set via page.addInitScript)
    if (
      typeof window !== 'undefined' &&
      (window as unknown as Record<string, unknown>).__LEXICONTEST_WS_URL__
    ) {
      return (window as unknown as Record<string, unknown>).__LEXICONTEST_WS_URL__ as string;
    }

    const env = import.meta.env.MODE;

    if (env === 'production') {
      return import.meta.env.VITE_WEBSOCKET_URL || 'wss://lexicontest.com/ws';
    }

    return WEBSOCKET_CONFIG.DEFAULT_URL;
  },

  /** Get API base URL based on environment */
  getApiBaseUrl: (): string => {
    // Runtime override for integration tests (set via page.addInitScript)
    if (
      typeof window !== 'undefined' &&
      (window as unknown as Record<string, unknown>).__LEXICONTEST_API_URL__
    ) {
      return (window as unknown as Record<string, unknown>).__LEXICONTEST_API_URL__ as string;
    }

    const env = import.meta.env.MODE;

    if (env === 'production') {
      return import.meta.env.VITE_API_URL || 'https://lexicontest.com';
    }

    return 'http://localhost:3000';
  },

  /**
   * Get mgmt API base URL. The mgmt SPA is served same-origin with /mgmt/api/*
   * from the server pod (issue #249), so relative URLs work everywhere by
   * default. The window override exists so per-test fixtures can point at an
   * isolated backend.
   */
  getMgmtApiBaseUrl: (): string => {
    if (
      typeof window !== 'undefined' &&
      (window as unknown as Record<string, unknown>).__LEXICONTEST_MGMT_API_URL__
    ) {
      return (window as unknown as Record<string, unknown>).__LEXICONTEST_MGMT_API_URL__ as string;
    }
    return '';
  },

  /** Get environment-specific timeout multiplier */
  getTimeoutMultiplier: (): number => {
    return import.meta.env.MODE === 'test' ? 0.1 : 1.0;
  },

  /** Get development server host with security validation */
  getDevServerHost: (): string => {
    const host = import.meta.env.VITE_DEV_HOST || DEV_SERVER_CONFIG.DEFAULT_HOST;
    if (!DEV_SERVER_CONFIG.VALID_HOSTS.includes(host)) {
      throw new Error(
        `Invalid development server host: ${host}. Allowed values: ${DEV_SERVER_CONFIG.VALID_HOSTS.join(', ')}`
      );
    }
    return host;
  },

  /** Get development server port with validation */
  getDevServerPort: (): number => {
    const port = parseInt(
      import.meta.env.VITE_DEV_PORT || DEV_SERVER_CONFIG.DEFAULT_PORT.toString()
    );
    if (isNaN(port) || port < DEV_SERVER_CONFIG.MIN_PORT || port > DEV_SERVER_CONFIG.MAX_PORT) {
      throw new Error(
        `Invalid development server port: ${port}. Must be between ${DEV_SERVER_CONFIG.MIN_PORT} and ${DEV_SERVER_CONFIG.MAX_PORT}`
      );
    }
    return port;
  },

  /** Check if network access is explicitly enabled */
  isNetworkAccessEnabled: (): boolean => {
    return import.meta.env.VITE_ALLOW_NETWORK === 'true';
  },

  /** Get security warning message when network access is enabled */
  getNetworkAccessWarning: (): string => {
    return 'WARNING: Development server is accessible from external networks. This should only be used for team development. Set VITE_ALLOW_NETWORK=false to restrict to localhost.';
  },
} as const;

/**
 * Validates configuration at application startup
 * Throws descriptive errors for missing/invalid configuration
 */
export const validateConfiguration = (): void => {
  // Validate WebSocket URL format
  const wsUrl = ENV_CONFIG.getWebSocketUrl();
  if (!wsUrl.startsWith('ws://') && !wsUrl.startsWith('wss://')) {
    throw new Error(`Invalid WebSocket URL: ${wsUrl}`);
  }

  // Validate numeric ranges
  if (WEBSOCKET_CONFIG.RECONNECT_ATTEMPTS < 1) {
    throw new Error('RECONNECT_ATTEMPTS must be >= 1');
  }

  if (WEBSOCKET_CONFIG.RECONNECT_DELAY < 100) {
    throw new Error('RECONNECT_DELAY must be >= 100ms');
  }

  if (WEBSOCKET_CONFIG.MAX_RECONNECT_DELAY < WEBSOCKET_CONFIG.RECONNECT_DELAY) {
    throw new Error('MAX_RECONNECT_DELAY must be >= RECONNECT_DELAY');
  }

  if (WEBSOCKET_CONFIG.BACKOFF_FACTOR < 1) {
    throw new Error('BACKOFF_FACTOR must be >= 1');
  }

  if (WEBSOCKET_CONFIG.DISCONNECT_DELAY < 100) {
    throw new Error('DISCONNECT_DELAY must be >= 100ms');
  }

  // Validate animation timing
  if (ANIMATION_CONFIG.LETTER_REVEAL_DURATION < 100) {
    throw new Error('LETTER_REVEAL_DURATION must be >= 100ms');
  }

  if (ANIMATION_CONFIG.BUTTON_FEEDBACK_DURATION < 50) {
    throw new Error('BUTTON_FEEDBACK_DURATION must be >= 50ms');
  }

  if (ANIMATION_CONFIG.LETTER_QUEUE_STAGGER_MS < 50) {
    throw new Error('LETTER_QUEUE_STAGGER_MS must be >= 50ms');
  }

  // Validate UI constraints
  if (UI_CONFIG.VALIDATION.PLAYER_NAME_MIN_LENGTH < 1) {
    throw new Error('PLAYER_NAME_MIN_LENGTH must be >= 1');
  }

  if (UI_CONFIG.VALIDATION.PLAYER_NAME_MAX_LENGTH < UI_CONFIG.VALIDATION.PLAYER_NAME_MIN_LENGTH) {
    throw new Error('PLAYER_NAME_MAX_LENGTH must be >= PLAYER_NAME_MIN_LENGTH');
  }

  // Validate game timing
  if (GAME_CONFIG.EXTENDED_SEARCH_TIMEOUT < 1000) {
    throw new Error('EXTENDED_SEARCH_TIMEOUT must be >= 1000ms');
  }

  if (GAME_CONFIG.MAX_SEARCH_TIMEOUT <= GAME_CONFIG.EXTENDED_SEARCH_TIMEOUT) {
    throw new Error('MAX_SEARCH_TIMEOUT must be > EXTENDED_SEARCH_TIMEOUT');
  }

  // Validate development server configuration
  try {
    ENV_CONFIG.getDevServerHost();
  } catch (error) {
    throw new Error(
      `Development server host validation failed: ${error instanceof Error ? error.message : String(error)}`
    );
  }

  try {
    ENV_CONFIG.getDevServerPort();
  } catch (error) {
    throw new Error(
      `Development server port validation failed: ${error instanceof Error ? error.message : String(error)}`
    );
  }

  // Log security warning if network access is enabled
  if (ENV_CONFIG.isNetworkAccessEnabled()) {
    console.warn(ENV_CONFIG.getNetworkAccessWarning());
  }
};

/**
 * Version check configuration for stale client detection
 */
export const VERSION_CHECK_CONFIG = {
  /** How often to poll /version.json (ms) */
  POLL_INTERVAL: 5 * 60 * 1000,
  /** Path to version.json (relative to origin) */
  VERSION_ENDPOINT: '/version.json',
} as const;

export type AuthConfigType = typeof AUTH_CONFIG;
export type AnimationConfigType = typeof ANIMATION_CONFIG;
export type WebSocketConfigType = typeof WEBSOCKET_CONFIG;
export type DevServerConfigType = typeof DEV_SERVER_CONFIG;
export type UIConfigType = typeof UI_CONFIG;
export type GameConfigType = typeof GAME_CONFIG;
export type VersionCheckConfigType = typeof VERSION_CHECK_CONFIG;
